/**
 * Students API
 * CRUD operations for student management
 */

import { apiRequest, type APIResponse } from './client';
import type { Student, CreateStudentData, UpdateStudentData, StudentCredentials } from '@/types/student';

/**
 * Get all students for the current teacher
 */
export const getStudents = async (userEmail: string): Promise<APIResponse<{ students: Student[] }>> => {
  return apiRequest('get_teacher_students', { userEmail });
};

/**
 * Get all students for a specific class
 */
export const getStudentsByClass = async (classId: string): Promise<APIResponse<{ students: Student[] }>> => {
  return apiRequest('get_class_students', { classId });
};

/**
 * Get all students for the school (admin only)
 */
export const getSchoolStudents = async (
  schoolId: string,
  adminTeacherId: string
): Promise<APIResponse<{ students: Student[] }>> => {
  return apiRequest('get_school_students', { schoolId, adminTeacherId });
};

/**
 * Get single student by ID
 */
export const getStudentById = async (studentId: string): Promise<APIResponse<{ student: Student }>> => {
  return apiRequest('get_student_by_id', { studentId });
};

/**
 * Create new student
 */
export const createStudent = async (
  data: CreateStudentData & { teacherEmail: string }
): Promise<APIResponse<{ student: Student; credentials: StudentCredentials }>> => {
  return apiRequest('create_student', data as unknown as Record<string, unknown>);
};

/**
 * Update existing student
 */
export const updateStudent = async (
  studentId: string,
  data: UpdateStudentData
): Promise<APIResponse<{ student: Student }>> => {
  return apiRequest('update_student', { studentId, ...data });
};

/**
 * Delete student
 */
export const deleteStudent = async (studentId: string): Promise<APIResponse<{ message: string }>> => {
  return apiRequest('delete_student', { studentId });
};

/**
 * Reset student password
 */
export const resetStudentPassword = async (
  studentId: string
): Promise<APIResponse<{ credentials: StudentCredentials }>> => {
  return apiRequest('reset_student_password', { studentId });
};
